import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useParams, useNavigate } from 'react-router-dom'
import api from '../../services/api'
import toast from 'react-hot-toast'
import {
  Folder,
  FolderOpen,
  File,
  Upload,
  Download,
  Trash2,
  FolderPlus,
  ArrowLeft,
  HardDrive,
  AlertCircle,
  Loader2,
  RefreshCw,
  BookOpen,
  Plus,
  ChevronRight,
  CheckCircle2,
  Circle,
  Clock,
  Send,
  MessageSquare,
  AlertTriangle,
  Paperclip,
  File as FileIcon,
  X,
  ClipboardList,
  Link as LinkIcon,
  FileText,
  ExternalLink,
} from 'lucide-react'
import LoadingSpinner from '../../components/LoadingSpinner'
import {
  AGENT_URL,
  fetchDeviceCode,
  fileTypeLabel,
  applicationLabel,
  useWorkspaceFileActions,
  useExtensionAppMap,
} from '../../hooks/useWorkspaceFileActions'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatBytes(bytes) {
  if (bytes === null || bytes === undefined) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

// Relative, human-readable modified date: Today / Yesterday / Sep 10.
// Matches My Files so a file reads identically in both places.
function formatDate(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  const now = new Date()
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const startOfYesterday = new Date(startOfToday.getTime() - 86400000)
  if (d >= startOfToday) return 'Today'
  if (d >= startOfYesterday) return 'Yesterday'
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

function statusConfig(status) {
  switch (status) {
    case 'completed':
      return { label: 'Completed', icon: CheckCircle2, color: 'text-green-600 dark:text-green-400' }
    case 'in_progress':
      return { label: 'In Progress', icon: Clock, color: 'text-orange-600 dark:text-orange-400' }
    default:
      return { label: 'Not Started', icon: Circle, color: 'text-gray-400 dark:text-gray-500' }
  }
}

// ─── Project List ────────────────────────────────────────────────────────────

function ProjectList({ deviceCode }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [showCreate, setShowCreate] = useState(false)

  const { data, isLoading, error } = useQuery({
    queryKey: ['digital-lab-projects'],
    queryFn: async () => {
      const { data } = await api.get('/digital-lab/student/projects')
      return data
    },
  })

  // Storage availability — when the configured work drive is unavailable,
  // students must not create new projects in the fallback location. The
  // Agent's ProgramData fallback remains for boot resilience, but new work
  // is blocked at the UI so a student doesn't unknowingly save into a
  // temporary location that will be invisible when the real drive returns.
  const { data: storage } = useQuery({
    queryKey: ['digital-lab-storage-status', deviceCode],
    queryFn: async () => {
      const res = await fetch(`${AGENT_URL}/workspace/storage`)
      if (!res.ok) throw new Error('Storage state unavailable')
      return res.json()
    },
    enabled: !!deviceCode,
    refetchInterval: 60 * 1000,
    retry: 1,
  })
  const storageUnavailable = storage?.state === 'unavailable'

  const { data: coursesData } = useQuery({
    queryKey: ['digital-lab-project-courses'],
    queryFn: async () => {
      const { data } = await api.get('/digital-lab/student/projects/courses/list')
      return data
    },
  })

  const projects = data?.projects || []
  const courses = coursesData?.courses || []

  // ── Recent Work — cross-project continuation ──────────────────────────────
  // The same Agent endpoint My Files uses. No new API, no index, no database:
  // the Agent walks the student's own workspace and returns the most recently
  // modified real files. Clicking one opens it natively through the shared hook.
  const { data: tokenData, refetch: refetchToken } = useQuery({
    queryKey: ['digital-lab-workspace-token', deviceCode],
    queryFn: async () => {
      const { data } = await api.post('/digital-lab/student/workspace/token', { device_code: deviceCode })
      return data
    },
    enabled: !!deviceCode,
    refetchInterval: 3 * 60 * 1000,
  })
  const workspaceToken = tokenData?.workspace_token

  const { data: appsData } = useQuery({
    queryKey: ['digital-lab-workspace-apps', deviceCode],
    queryFn: async () => {
      const { data } = await api.get('/digital-lab/student/applications')
      return data
    },
    enabled: !!deviceCode,
    staleTime: 5 * 60 * 1000,
  })
  const extAppMap = useExtensionAppMap(appsData)

  const { withWorkspaceTokenRetry, openFile, opening } = useWorkspaceFileActions({
    workspaceToken,
    refetchToken,
    deviceCode,
    extAppMap,
  })

  const { data: recentData } = useQuery({
    queryKey: ['digital-lab-recent-work', workspaceToken],
    queryFn: async () => {
      const res = await withWorkspaceTokenRetry((token) =>
        fetch(`${AGENT_URL}/workspace/recent?token=${token}&limit=6`)
      )
      const body = await res.json()
      if (!res.ok) throw new Error(body.error || 'Failed to load recent work')
      return body
    },
    enabled: !!workspaceToken,
    // A file saved from Photoshop should be here when the student comes back.
    refetchOnWindowFocus: true,
    retry: 1,
  })
  const recentFiles = recentData?.entries || []

  // Map a workspace-relative path back to the project it belongs to, so a
  // recent file reads "logo.psd — Logo Design · Photoshop" rather than exposing
  // "logo-design/Assets" or legacy "Projects/logo-design/Assets". Falls back
  // silently for files saved outside a project folder.
  const projectByIdentifier = {}
  for (const p of projects) {
    if (p.workspace_identifier) projectByIdentifier[p.workspace_identifier] = p
  }
  const describeRecent = (entryPath) => {
    const parts = (entryPath || '').split('/')
    // Direct project folder: <workspace_identifier>/...
    if (parts[0] && projectByIdentifier[parts[0]]) {
      return projectByIdentifier[parts[0]].title
    }
    // Legacy structure: Projects/<workspace_identifier>/...
    if (parts[0] === 'Projects' && parts.length > 2) {
      const project = projectByIdentifier[parts[1]]
      if (project) return project.title
    }
    // Files saved outside a project folder (imported, or left in My Work) have
    // no project to name. Return null so the row omits the segment rather than
    // padding it with a label that tells the student nothing.
    return null
  }

  if (isLoading) return <LoadingSpinner />

  if (error) {
    return (
      <div className="w-full max-w-[1600px] mx-auto">
        <div className="cinema-empty">
          <AlertCircle size={36} className="mx-auto mb-4 text-red-400/70" />
          <p className="font-sans text-sm text-cinema-soft" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            We couldn't load your projects right now.
          </p>
          <Link to="/student/digital-lab" className="cinema-btn-text mt-5">
            <ArrowLeft size={16} /> Back to Digital Lab
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full max-w-[1280px]">
      {/* Header */}
      <div className="cinema-page-header">
        <div>
          <span className="cinema-label block mb-2">Student · Digital Lab</span>
          <h1
            className="text-2xl sm:text-3xl xl:text-4xl text-white"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800 }}
          >
            Projects
          </h1>
          <p
            className="font-sans text-sm text-cinema-muted mt-2 max-w-xl"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
          >
            Your creative projects on this SMA workstation. Open a project to work
            with its files.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 shrink-0">
          <Link to="/student/digital-lab" className="cinema-btn-ghost text-xs sm:text-sm px-5 py-3 min-h-[44px]">
            <ArrowLeft size={16} /> Digital Lab
          </Link>
          <button
            onClick={() => setShowCreate((v) => !v)}
            disabled={storageUnavailable}
            className="cinema-btn-primary text-xs sm:text-sm px-5 py-3 min-h-[44px] disabled:opacity-40 disabled:cursor-not-allowed"
            title={storageUnavailable ? 'Workspace unavailable — contact your instructor' : undefined}
          >
            <Plus size={16} /> New Project
          </button>
        </div>
      </div>

      {/* Storage unavailable banner — blocks new project creation */}
      {storageUnavailable && (
        <div className="border-l-2 border-amber-500/60 bg-amber-500/[0.06] px-4 py-3 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle size={18} className="text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="font-sans text-sm text-cinema-soft" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Your Digital Lab workspace is currently unavailable. Your existing work has not been lost.
              </p>
              <p className="font-sans text-xs text-cinema-muted mt-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {storage?.message ||
                  'Please contact your instructor or administrator before starting new work.'}
              </p>
              <p className="font-sans text-xs text-cinema-muted mt-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                New project creation is paused until the workspace drive is restored.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Create form — hidden when storage unavailable */}
      {showCreate && !storageUnavailable && (
        <div className="mb-12">
          <CreateProjectForm
            courses={courses}
            onCancel={() => setShowCreate(false)}
            onSuccess={(created) => {
              setShowCreate(false)
              queryClient.invalidateQueries({ queryKey: ['digital-lab-projects'] })
              // Straight into the new project's workspace — the project record
              // and its real local folder are created by the existing flow.
              if (created?.project?.id) {
                navigate(`/student/digital-lab/projects/${created.project.id}`)
              }
            }}
          />
        </div>
      )}

      {/* ─── Projects ────────────────────────────────────────────────────── */}
      <section className="mb-12">
        {projects.length === 0 ? (
          <div className="cinema-empty">
            <FolderOpen size={32} className="mx-auto mb-4 text-cinema-muted/50" />
            <p className="font-sans text-sm text-cinema-soft" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              You don't have any projects yet.
            </p>
            <p
              className="font-sans text-xs text-cinema-muted mt-2 max-w-md mx-auto"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            >
              A project is a real folder on this workstation. Create one, and
              anything you save into it from Photoshop, Premiere or any other
              application appears here.
            </p>
            <button
              onClick={() => setShowCreate(true)}
              disabled={storageUnavailable}
              className="cinema-btn-ghost text-sm px-6 py-3 min-h-[44px] mt-6 disabled:opacity-40 disabled:cursor-not-allowed"
              title={storageUnavailable ? 'Workspace unavailable — contact your instructor' : undefined}
            >
              <Plus size={16} /> New Project
            </button>
          </div>
        ) : (
          <div className="cinema-list">
            {projects.map((project) => {
              const sc = statusConfig(project.status)
              const StatusIcon = sc.icon
              return (
                <button
                  key={project.id}
                  type="button"
                  onClick={() => navigate(`/student/digital-lab/projects/${project.id}`)}
                  className="cinema-list-row group w-full text-left px-3 -mx-3 border-l border-transparent hover:border-cinema-gold/40 hover:bg-white/[0.025] transition-colors cursor-pointer"
                >
                  <Folder
                    size={18}
                    className="shrink-0 text-cinema-gold/70 group-hover:text-cinema-gold transition-colors"
                  />
                  <div className="flex-1 min-w-0">
                    <span className="block font-sans text-sm text-white font-medium truncate">
                      {project.title}
                    </span>
                    <span
                      className="block font-sans text-xs text-cinema-muted mt-0.5 truncate"
                      style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                    >
                      {project.course ? project.course.title : 'Personal project'}
                      <span className="sm:hidden"> · {sc.label}</span>
                    </span>
                  </div>
                  {/* Status and date collapse away on narrow screens rather than
                      squeezing the project name, which is the point of the row. */}
                  <span className="hidden sm:flex items-center gap-1.5 shrink-0">
                    <StatusIcon size={14} className={sc.color} />
                    <span
                      className={`font-sans text-xs ${sc.color}`}
                      style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                    >
                      {sc.label}
                    </span>
                  </span>
                  <span
                    className="hidden md:block font-sans text-xs text-cinema-muted shrink-0 w-24 text-right"
                    style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                  >
                    {formatDate(project.updated_at)}
                  </span>
                  <ChevronRight
                    size={16}
                    className="text-cinema-muted/30 group-hover:text-cinema-gold/60 transition-colors shrink-0"
                  />
                </button>
              )
            })}
          </div>
        )}
      </section>

      {/* ─── Recent Work — continue where you left off ────────────────────── */}
      {recentFiles.length > 0 && (
        <section className="mb-12">
          <div className="flex items-center gap-2 mb-5">
            <Clock size={14} className="text-cinema-glow" />
            <span className="cinema-label text-cinema-glow">Recent Work</span>
          </div>
          <div className="cinema-list">
            {recentFiles.map((entry, idx) => {
              const isOpening = opening === entry.name
              const dirPath = entry.path.includes('/')
                ? entry.path.slice(0, entry.path.lastIndexOf('/'))
                : ''
              return (
                <div
                  key={`recent-${idx}`}
                  className="cinema-list-row group px-3 -mx-3 border-l border-transparent hover:border-cinema-gold/40 hover:bg-white/[0.025] transition-colors"
                >
                  <File size={18} className="text-cinema-muted group-hover:text-cinema-gold transition-colors shrink-0" />
                  <button
                    type="button"
                    onClick={() => openFile({ name: entry.name }, dirPath)}
                    disabled={isOpening}
                    className="flex-1 min-w-0 text-left disabled:cursor-wait"
                  >
                    <span className="block font-sans text-sm text-white font-medium truncate">
                      {entry.name}
                    </span>
                    <span
                      className="block font-sans text-xs text-cinema-muted mt-0.5 truncate"
                      style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                    >
                      {[describeRecent(entry.path), applicationLabel(entry.name), formatDate(entry.modified)]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </button>
                  {isOpening ? (
                    <Loader2 size={16} className="text-cinema-glow animate-spin shrink-0" />
                  ) : (
                    <ChevronRight size={16} className="text-cinema-muted/30 group-hover:text-cinema-gold/60 transition-colors shrink-0" />
                  )}
                </div>
              )
            })}
          </div>
        </section>
      )}

      {/* Quiet footnote — the student should know where their work lives
          without being shown a filesystem path. */}
      <p
        className="font-sans text-xs text-cinema-muted/70 flex items-start gap-2"
        style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
      >
        <HardDrive size={14} className="shrink-0 mt-0.5" />
        Your projects are stored on this SMA workstation. Cloud backup and
        synchronisation are not yet available.
      </p>
    </div>
  )
}

// ─── Create Project Form ─────────────────────────────────────────────────────

function CreateProjectForm({ courses, onCancel, onSuccess }) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [courseId, setCourseId] = useState('')

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const { data: result } = await api.post('/digital-lab/student/projects', data)
      return result
    },
    onSuccess: (data) => {
      toast.success('Project created')
      // Hand the created project back so the caller can take the student
      // straight into its workspace.
      onSuccess(data)
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Failed to create project')
    },
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!title.trim()) return
    createMutation.mutate({
      title: title.trim(),
      description: description.trim() || null,
      course_id: courseId ? parseInt(courseId, 10) : null,
    })
  }

  const fieldClass =
    'w-full px-4 py-3 text-sm bg-white/[0.03] border border-white/10 text-white ' +
    'placeholder:text-cinema-muted/60 focus:outline-none focus:border-cinema-gold/50 transition-colors'
  const labelClass = 'cinema-label block mb-2'

  return (
    <form onSubmit={handleSubmit} className="border-t border-b border-white/5 py-8 space-y-6">
      <div>
        <span className="cinema-label text-cinema-glow block mb-2">New Project</span>
        <p
          className="font-sans text-xs text-cinema-muted max-w-md"
          style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
        >
          A real folder is created for this project on this workstation.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <label className={labelClass}>Project Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Brand Identity Project"
            className={fieldClass}
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            autoFocus
            required
          />
        </div>
        <div>
          <label className={labelClass}>Course (optional)</label>
          <select
            value={courseId}
            onChange={(e) => setCourseId(e.target.value)}
            className={fieldClass}
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
          >
            <option value="">No course</option>
            {courses.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className={labelClass}>Description (optional)</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What is this project about?"
          rows={2}
          className={fieldClass}
          style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={createMutation.isPending || !title.trim()}
          className="cinema-btn-primary text-sm px-6 py-3 min-h-[44px] disabled:opacity-50"
        >
          {createMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
          {createMutation.isPending ? 'Creating…' : 'Create Project'}
        </button>
        <button type="button" onClick={onCancel} className="cinema-btn-text">
          Cancel
        </button>
      </div>
    </form>
  )
}

// ─── Submission status ───────────────────────────────────────────────────────

function submissionStatusConfig(status) {
  switch (status) {
    case 'reviewed':
      return { label: 'Reviewed', icon: CheckCircle2, color: 'text-green-600 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-900/20', border: 'border-green-200 dark:border-green-800/50' }
    case 'needs_changes':
      return { label: 'Needs Changes', icon: AlertTriangle, color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-900/20', border: 'border-orange-200 dark:border-orange-800/50' }
    case 'submitted':
      return { label: 'Submitted', icon: Clock, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200 dark:border-blue-800/50' }
    default:
      return { label: 'Not Submitted', icon: Circle, color: 'text-gray-400 dark:text-gray-500', bg: 'bg-gray-50 dark:bg-gray-800/50', border: 'border-gray-200 dark:border-gray-700' }
  }
}

function SubmissionSection({ projectId }) {
  const queryClient = useQueryClient()
  const [showConfirm, setShowConfirm] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState([])
  const [uploadingFiles, setUploadingFiles] = useState(false)

  const { data: submissionData, isLoading } = useQuery({
    queryKey: ['digital-lab-project-submission', projectId],
    queryFn: async () => {
      const { data } = await api.get(`/digital-lab/student/projects/${projectId}/submission`)
      return data
    },
  })

  const submission = submissionData?.submission

  const { data: filesData } = useQuery({
    queryKey: ['digital-lab-submission-files', submission?.id],
    queryFn: async () => {
      const { data } = await api.get(`/digital-lab/student/submissions/${submission.id}/files`)
      return data
    },
    enabled: !!submission?.id,
  })

  const submitMutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.post(`/digital-lab/student/projects/${projectId}/submit`)
      return data
    },
    onSuccess: async (data) => {
      // Upload selected files to the new submission
      if (selectedFiles.length > 0 && data.submission.id) {
        setUploadingFiles(true)
        try {
          for (const file of selectedFiles) {
            const formData = new FormData()
            formData.append('file', file)
            await api.post(`/digital-lab/student/submissions/${data.submission.id}/files`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
            })
          }
          toast.success(`Project submitted with ${selectedFiles.length} file${selectedFiles.length !== 1 ? 's' : ''}`)
        } catch (uploadErr) {
          toast.error(uploadErr.response?.data?.error || 'Some files failed to upload. You can retry from the submission.')
        } finally {
          setUploadingFiles(false)
          setSelectedFiles([])
        }
      } else {
        toast.success('Project submitted for instructor review')
      }
      setShowConfirm(false)
      queryClient.invalidateQueries({ queryKey: ['digital-lab-project-submission', projectId] })
      queryClient.invalidateQueries({ queryKey: ['digital-lab-submission-files', data.submission.id] })
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Failed to submit project')
      setShowConfirm(false)
    },
  })

  const uploadFileMutation = useMutation({
    mutationFn: async ({ submissionId, file }) => {
      const formData = new FormData()
      formData.append('file', file)
      const { data } = await api.post(`/digital-lab/student/submissions/${submissionId}/files`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return data
    },
    onSuccess: () => {
      toast.success('File uploaded')
      queryClient.invalidateQueries({ queryKey: ['digital-lab-submission-files', submission?.id] })
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Failed to upload file')
    },
  })

  const deleteFileMutation = useMutation({
    mutationFn: async ({ submissionId, fileId }) => {
      await api.delete(`/digital-lab/student/submissions/${submissionId}/files/${fileId}`)
    },
    onSuccess: () => {
      toast.success('File removed')
      queryClient.invalidateQueries({ queryKey: ['digital-lab-submission-files', submission?.id] })
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Failed to remove file')
    },
  })

  if (isLoading) {
    return (
      <div className="border-t border-white/5 pt-6">
        <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
      </div>
    )
  }

  const sc = submissionStatusConfig(submission?.status)
  const submittedFiles = filesData?.files || []
  const totalSize = submittedFiles.reduce((sum, f) => sum + f.size_bytes, 0)

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || [])
    setSelectedFiles((prev) => [...prev, ...files])
  }

  const removeSelectedFile = (idx) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== idx))
  }

  const handleAddFileToSubmission = (e) => {
    const files = Array.from(e.target.files || [])
    for (const file of files) {
      uploadFileMutation.mutate({ submissionId: submission.id, file })
    }
  }

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
  }

  return (
    <div className={`border-l-2 ${sc.border} pl-5 py-1`}>
      <div className="flex items-center gap-2 mb-4">
        <Send className="w-5 h-5 text-gray-500 dark:text-gray-400" />
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700 }}>
          Submission
        </h2>
      </div>

      {!submission ? (
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            When your project is ready, submit it for instructor review.
          </p>

          {/* File selection */}
          <div className="mb-4">
            <p className="text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 font-medium mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Select files your instructor should review
            </p>
            <label className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer">
              <Paperclip className="w-4 h-4" /> Choose Files
              <input type="file" multiple className="hidden" onChange={handleFileSelect} />
            </label>
          </div>

          {/* Selected files list */}
          {selectedFiles.length > 0 && (
            <div className="mb-4 space-y-2">
              {selectedFiles.map((file, idx) => (
                <div key={idx} className="flex items-center gap-3 bg-white dark:bg-gray-900 rounded-lg border border-gray-100 dark:border-gray-800 px-4 py-2">
                  <FileIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="flex-1 text-sm text-gray-700 dark:text-gray-200 truncate" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                    {file.name}
                  </span>
                  <span className="text-xs text-gray-400 dark:text-gray-500" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                    {formatFileSize(file.size)}
                  </span>
                  <button
                    onClick={() => removeSelectedFile(idx)}
                    className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <p className="text-xs text-gray-400 dark:text-gray-500" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Total: {formatFileSize(selectedFiles.reduce((sum, f) => sum + f.size, 0))}
              </p>
            </div>
          )}

          {!showConfirm ? (
            <button
              onClick={() => setShowConfirm(true)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            >
              <Send className="w-4 h-4" /> Submit Project
            </button>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-700 dark:text-gray-200" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Make sure your project is complete before submitting it for instructor review.
                {selectedFiles.length > 0 && ` ${selectedFiles.length} file${selectedFiles.length !== 1 ? 's' : ''} will be uploaded.`}
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => submitMutation.mutate()}
                  disabled={submitMutation.isPending || uploadingFiles}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50"
                  style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                >
                  {submitMutation.isPending || uploadingFiles ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {uploadingFiles ? 'Uploading...' : 'Confirm Submit'}
                </button>
                <button
                  onClick={() => setShowConfirm(false)}
                  className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                  style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <sc.icon className={`w-4 h-4 ${sc.color}`} />
            <span className={`text-sm font-medium ${sc.color}`} style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {sc.label}
            </span>
            <span className="text-xs text-gray-400 dark:text-gray-500 ml-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {submission.submitted_at && new Date(submission.submitted_at).toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
          </div>

          {submission.status === 'submitted' && (
            <p className="text-sm text-gray-500 dark:text-gray-400" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Your project has been submitted and is waiting for instructor review. You can still add or remove files while waiting.
            </p>
          )}

          {submission.status === 'reviewed' && (
            <p className="text-sm text-gray-500 dark:text-gray-400" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Your instructor has reviewed this submission. No further action is needed unless you want to make changes and submit again.
            </p>
          )}

          {submission.status === 'needs_changes' && (
            <div className={`rounded-lg ${sc.bg} ${sc.border} border p-4`}>
              <p className="text-sm font-medium text-orange-700 dark:text-orange-300 mb-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Changes required
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-300" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Your instructor has reviewed your submission and requested changes. Read the feedback below, update your project files, then select your revised files and resubmit.
              </p>
            </div>
          )}

          {/* Submitted files */}
          {submittedFiles.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <FileIcon className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                <span className="text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 font-medium" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Submitted Files · {submittedFiles.length} file{submittedFiles.length !== 1 ? 's' : ''} · {formatFileSize(totalSize)}
                </span>
              </div>
              <div className="space-y-2">
                {submittedFiles.map((file) => (
                  <div key={file.id} className="flex items-center gap-3 bg-white dark:bg-gray-900 rounded-lg border border-gray-100 dark:border-gray-800 px-4 py-2">
                    <FileIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="flex-1 text-sm text-gray-700 dark:text-gray-200 truncate" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                      {file.original_filename}
                    </span>
                    <span className="text-xs text-gray-400 dark:text-gray-500" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                      {formatFileSize(file.size_bytes)}
                    </span>
                    {submission.status === 'submitted' && (
                      <button
                        onClick={() => deleteFileMutation.mutate({ submissionId: submission.id, fileId: file.id })}
                        disabled={deleteFileMutation.isPending}
                        className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add more files while submission is awaiting review */}
          {submission.status === 'submitted' && (
            <div>
              <label className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer">
                <Upload className="w-3.5 h-3.5" /> Add More Files
                <input type="file" multiple className="hidden" onChange={handleAddFileToSubmission} />
              </label>
            </div>
          )}

          {submission.feedback && (
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-100 dark:border-gray-800 p-4">
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                <span className="text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 font-medium" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Your Instructor's Feedback
                </span>
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {submission.feedback}
              </p>
              <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-50 dark:border-gray-800/50">
                {submission.reviewed_by_name && (
                  <span className="text-xs text-gray-400 dark:text-gray-500" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                    Reviewed by {submission.reviewed_by_name}
                  </span>
                )}
                {submission.reviewed_at && (
                  <span className="text-xs text-gray-400 dark:text-gray-500" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                    {new Date(submission.reviewed_at).toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' })}
                  </span>
                )}
              </div>
            </div>
          )}

          {submission.status === 'needs_changes' && (
            <div className="pt-2 border-t border-gray-100 dark:border-gray-800">
              <p className="text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 font-medium mb-3" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Submit Your Revised Project
              </p>
              {/* File selection for resubmission */}
              {selectedFiles.length > 0 && (
                <div className="mb-4 space-y-2">
                  <p className="text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 font-medium mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                    New files for resubmission
                  </p>
                  {selectedFiles.map((file, idx) => (
                    <div key={idx} className="flex items-center gap-3 bg-white dark:bg-gray-900 rounded-lg border border-gray-100 dark:border-gray-800 px-4 py-2">
                      <FileIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="flex-1 text-sm text-gray-700 dark:text-gray-200 truncate" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                        {file.name}
                      </span>
                      <span className="text-xs text-gray-400 dark:text-gray-500" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                        {formatFileSize(file.size)}
                      </span>
                      <button
                        onClick={() => removeSelectedFile(idx)}
                        className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-3 flex-wrap">
                <label className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer">
                  <Paperclip className="w-4 h-4" /> Choose Files
                  <input type="file" multiple className="hidden" onChange={handleFileSelect} />
                </label>
                <button
                  onClick={() => submitMutation.mutate()}
                  disabled={submitMutation.isPending || uploadingFiles}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50"
                  style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                >
                  {submitMutation.isPending || uploadingFiles ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {uploadingFiles ? 'Uploading...' : 'Resubmit Project'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Project Context (Slice 10) ─────────────────────────────────────────────

function ProjectContext({ projectId }) {
  const { data: contextData, isLoading } = useQuery({
    queryKey: ['digital-lab-project-context', projectId],
    queryFn: async () => {
      const { data } = await api.get(`/digital-lab/student/projects/${projectId}/context`)
      return data
    },
    enabled: !!projectId,
  })

  if (isLoading) return null

  const assignment = contextData?.assignment
  const resources = contextData?.resources || []

  // Don't render the section if there's nothing to show
  if (!assignment && resources.length === 0) return null

  return (
    <div className="space-y-6">
      {/* Project Brief / Assignment */}
      {assignment && (
        <div className="border-t border-white/5 pt-6 space-y-4">
          <div className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-orange-500 dark:text-orange-400" />
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700 }}>
              Project Brief
            </h2>
          </div>
          <div className="space-y-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 font-medium mb-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Assignment
              </p>
              <p className="text-sm font-medium text-gray-900 dark:text-white" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {assignment.title}
              </p>
              {assignment.lesson_title && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  From lesson:{' '}
                  {assignment.lesson_id && assignment.course_id ? (
                    <Link
                      to={`/student/courses/${assignment.course_id}?lesson=${assignment.lesson_id}`}
                      className="text-orange-500 dark:text-orange-400 hover:text-orange-600 dark:hover:text-orange-300 underline-offset-2 hover:underline"
                    >
                      {assignment.lesson_title}
                    </Link>
                  ) : (
                    assignment.lesson_title
                  )}
                </p>
              )}
            </div>
            {assignment.description && (
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 font-medium mb-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Instructions
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  {assignment.description}
                </p>
              </div>
            )}
            {assignment.requirements && (
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 font-medium mb-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Requirements
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  {assignment.requirements}
                </p>
              </div>
            )}
            <div className="flex items-center gap-4 pt-2 border-t border-gray-50 dark:border-gray-800/50">
              {assignment.due_date && (
                <span className="text-xs text-gray-400 dark:text-gray-500" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  <Clock className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                  Due {new Date(assignment.due_date).toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' })}
                </span>
              )}
              {assignment.assignment_type && assignment.assignment_type !== 'standard' && (
                <span className="text-xs text-gray-400 dark:text-gray-500" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Type: {assignment.assignment_type}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Resources */}
      {resources.length > 0 && (
        <div className="border-t border-white/5 pt-6 space-y-4">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-orange-500 dark:text-orange-400" />
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700 }}>
              Resources
            </h2>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Use these resources while working on your project.
          </p>
          <div className="space-y-2">
            {resources.map((resource) => (
              <div key={resource.id} className="flex items-center gap-3 border-b border-white/5 py-3">
                {resource.type === 'link' ? (
                  <LinkIcon className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                ) : (
                  <FileIcon className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-700 dark:text-gray-200 truncate" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                    {resource.title}
                  </p>
                  {resource.lesson_title && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 truncate" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                      {resource.lesson_title}
                    </p>
                  )}
                </div>
                <span className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  {resource.type}
                </span>
                <a
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-800/50 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/20"
                  style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Open
                </a>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Project Detail ──────────────────────────────────────────────────────────

function ProjectDetail({ deviceCode }) {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [currentPath, setCurrentPath] = useState('')
  const [showCreateFolder, setShowCreateFolder] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [uploadFile, setUploadFile] = useState(null)
  const [workspaceInitialized, setWorkspaceInitialized] = useState(false)
  // Guard so workspace auto-init only fires once per project load (Slice 13)
  const autoInitTriedRef = useRef(false)

  // Get project details
  const { data: projectData, isLoading, error } = useQuery({
    queryKey: ['digital-lab-project', id],
    queryFn: async () => {
      const { data } = await api.get(`/digital-lab/student/projects/${id}`)
      return data
    },
  })

  // Get workspace token
  // The backend workspace token expires in 5 minutes. Refresh every 3 so there
  // is a comfortable margin, and allow react-query's default retry so a single
  // network blip doesn't strand the token (a stale token is what produces the
  // "nothing works until I reload" class of bug on a page a student leaves
  // open while working in Premiere).
  const { data: tokenData, refetch: refetchToken } = useQuery({
    queryKey: ['digital-lab-workspace-token', deviceCode],
    queryFn: async () => {
      const { data } = await api.post('/digital-lab/student/workspace/token', { device_code: deviceCode })
      return data
    },
    enabled: !!deviceCode,
    refetchInterval: 3 * 60 * 1000,
  })

  const workspaceToken = tokenData?.workspace_token
  const project = projectData?.project
  const projectPath = project?.workspace_identifier || ''

  // Approved applications on this workstation → extension map, so a double
  // click opens the right native application. Application-agnostic: anything
  // unmapped falls through to the Windows default association.
  const { data: appsData } = useQuery({
    queryKey: ['digital-lab-workspace-apps', deviceCode],
    queryFn: async () => {
      const { data } = await api.get('/digital-lab/student/applications')
      return data
    },
    enabled: !!deviceCode,
    staleTime: 5 * 60 * 1000,
  })
  const extAppMap = useExtensionAppMap(appsData)

  // Shared workspace file actions — the same implementation My Files uses.
  const {
    withWorkspaceTokenRetry,
    openFile,
    revealFolder,
    downloadFile,
    opening,
    revealing,
  } = useWorkspaceFileActions({ workspaceToken, refetchToken, deviceCode, extAppMap })

  // The folder the student is currently looking at, workspace-relative.
  const currentFolderPath = currentPath ? `${projectPath}/${currentPath}` : projectPath

  // Initialize project workspace
  const initWorkspaceMutation = useMutation({
    mutationFn: async () => {
      // First, call backend to verify ownership and get workspace_identifier
      const { data: initRes } = await api.post(`/digital-lab/student/projects/${id}/workspace`)
      // Then, call Agent to create the folder
      const res = await fetch(`${AGENT_URL}/workspace/project`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workspace_token: workspaceToken,
          workspace_identifier: initRes.workspace_identifier,
        }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to initialize workspace')
      return { initRes, agentRes: data }
    },
    onSuccess: () => {
      toast.success('Project workspace created')
      setWorkspaceInitialized(true)
      queryClient.invalidateQueries({ queryKey: ['digital-lab-project-files'] })
    },
    onError: () => toast.error('We couldn\'t open your workspace right now.'),
  })

  // ── Auto-initialize workspace on arrival (Slice 13) ───────────────────────
  // When a student clicks Continue Learning, they should land in a ready
  // workspace — not be asked to click "Open Project Workspace" first. This
  // effect fires the init mutation once per project load, as soon as the
  // workspace token is available. The ref guard prevents retries on every
  // query refetch.
  useEffect(() => {
    if (
      !autoInitTriedRef.current &&
      !workspaceInitialized &&
      !initWorkspaceMutation.isPending &&
      workspaceToken &&
      project
    ) {
      autoInitTriedRef.current = true
      initWorkspaceMutation.mutate()
    }
  }, [workspaceInitialized, workspaceToken, project, initWorkspaceMutation])

  // List project files from Agent
  const {
    data: listingData,
    isLoading: listingLoading,
    refetch: refetchListing,
    error: listingError,
  } = useQuery({
    queryKey: ['digital-lab-project-files', workspaceToken, projectPath, currentPath],
    queryFn: async () => {
      const fullPath = currentPath ? `${projectPath}/${currentPath}` : projectPath
      const res = await withWorkspaceTokenRetry((token) => {
        const params = new URLSearchParams({ token, path: fullPath })
        return fetch(`${AGENT_URL}/workspace?${params}`)
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to list files')
      return data
    },
    enabled: !!workspaceToken && !!project && workspaceInitialized,
    // The filesystem is the source of truth, and native applications write to
    // it behind our back. Refetching when the window regains focus is what
    // makes a file saved from Photoshop or Premiere simply be there when the
    // student alt-tabs back — without them learning to press Refresh.
    refetchOnWindowFocus: true,
    refetchInterval: 15 * 1000,
    retry: 1,
  })

  // Create folder mutation
  const createFolderMutation = useMutation({
    mutationFn: async (folderPath) => {
      const fullPath = `${projectPath}/${currentPath ? currentPath + '/' : ''}${folderPath}`
      const res = await withWorkspaceTokenRetry((token) =>
        fetch(`${AGENT_URL}/workspace/folders`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ workspace_token: token, path: fullPath }),
        })
      )
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to create folder')
      return data
    },
    onSuccess: () => {
      toast.success('Folder created')
      setShowCreateFolder(false)
      setNewFolderName('')
      queryClient.invalidateQueries({ queryKey: ['digital-lab-project-files'] })
    },
    onError: (err) => toast.error(err.message),
  })

  // Upload file mutation
  const uploadFileMutation = useMutation({
    mutationFn: async ({ file, targetPath }) => {
      const content_base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
          const result = reader.result
          resolve(result.substring(result.indexOf(',') + 1))
        }
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
      const res = await withWorkspaceTokenRetry((token) =>
        fetch(`${AGENT_URL}/workspace/files`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workspace_token: token,
            path: targetPath,
            content_base64,
          }),
        })
      )
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to upload file')
      return data
    },
    onSuccess: () => {
      toast.success('File uploaded')
      setUploadFile(null)
      queryClient.invalidateQueries({ queryKey: ['digital-lab-project-files'] })
    },
    onError: (err) => toast.error(err.message),
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (itemPath) => {
      const res = await withWorkspaceTokenRetry((token) =>
        fetch(
          `${AGENT_URL}/workspace/files?token=${token}&path=${encodeURIComponent(itemPath)}`,
          { method: 'DELETE' }
        )
      )
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to delete')
      return data
    },
    onSuccess: () => {
      toast.success('Deleted')
      queryClient.invalidateQueries({ queryKey: ['digital-lab-project-files'] })
    },
    onError: (err) => toast.error(err.message),
  })

  // Status update mutation
  const updateStatusMutation = useMutation({
    mutationFn: async (status) => {
      const { data } = await api.patch(`/digital-lab/student/projects/${id}`, { status })
      return data
    },
    onSuccess: () => {
      toast.success('Status updated')
      queryClient.invalidateQueries({ queryKey: ['digital-lab-project'] })
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to update status'),
  })

  const handleDownload = downloadFile

  const handleUpload = () => {
    if (!uploadFile) return
    const targetPath = `${projectPath}/${currentPath ? currentPath + '/' : ''}${uploadFile.name}`
    uploadFileMutation.mutate({ file: uploadFile, targetPath })
  }

  const navigateInto = (folderName) => {
    setCurrentPath((prev) => (prev ? `${prev}/${folderName}` : folderName))
  }

  const navigateUp = () => {
    setCurrentPath((prev) => {
      if (!prev) return prev
      const parts = prev.split('/')
      parts.pop()
      return parts.join('/')
    })
  }

  if (isLoading) return <LoadingSpinner />

  if (error || !project) {
    return (
      <div className="empty-state py-16">
        <AlertCircle className="w-10 h-10 text-red-400 dark:text-red-500 mx-auto mb-3" />
        <p className="text-gray-600 dark:text-gray-300" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          We couldn't load your project right now.
        </p>
        <Link to="/student/digital-lab/projects" className="inline-flex items-center gap-2 mt-4 text-sm text-orange-600 dark:text-orange-400 hover:underline">
          <ArrowLeft className="w-4 h-4" /> Back to Projects
        </Link>
      </div>
    )
  }

  const sc = statusConfig(project.status)
  const StatusIcon = sc.icon
  const entries = listingData?.entries || []
  // Folders first, then files — each alphabetical. This is the ordering every
  // file manager the student has ever used produces, so it needs no learning.
  const folderEntries = entries
    .filter((e) => e.type === 'folder')
    .sort((a, b) => a.name.localeCompare(b.name))
  const fileEntries = entries
    .filter((e) => e.type !== 'folder')
    .sort((a, b) => a.name.localeCompare(b.name))
  const pathParts = currentPath ? currentPath.split('/').filter(Boolean) : []
  const storage = listingData?.storage
  const storageUnavailable = storage && storage.state !== 'ok'
  // The Explorer escape hatch appears only when this workstation's Agent says
  // it is enabled. Default is off — see isExplorerRevealEnabled() in agent.js.
  const explorerEnabled = listingData?.explorer_reveal_enabled === true

  return (
    <div className="w-full max-w-[1600px] mx-auto">
      {/* Back — one clear way out, to the project browser */}
      <div className="flex items-center gap-2 flex-wrap mb-6">
        <Link to="/student/digital-lab/projects" className="cinema-btn-text">
          <ArrowLeft size={16} /> Projects
        </Link>
      </div>

      {/* ─── Project header ──────────────────────────────────────────────────
          The project is the object, not the database record. Course, status and
          description sit underneath as quiet context — present for orientation,
          never competing with the work itself. */}
      <div className="cinema-page-header">
        <div className="min-w-0">
          <span className="cinema-label block mb-2">Local project workspace</span>
          <h1
            className="text-2xl sm:text-3xl xl:text-4xl text-white break-words"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800 }}
          >
            {project.title}
          </h1>
          <div
            className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 font-sans text-xs text-cinema-muted"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
          >
            {project.course && (
              <>
                <span className="inline-flex items-center gap-1.5">
                  <BookOpen size={13} /> {project.course.title}
                </span>
                <span className="text-cinema-muted/40">·</span>
              </>
            )}
            <span className={`inline-flex items-center gap-1.5 ${sc.color}`}>
              <StatusIcon size={13} /> {sc.label}
            </span>
          </div>
          {project.description && (
            <p
              className="font-sans text-sm text-cinema-muted mt-3 max-w-2xl"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            >
              {project.description}
            </p>
          )}
        </div>

        {/* Status — a quiet control, not a headline. */}
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {['not_started', 'in_progress', 'completed'].map((st) => {
            const cfg = statusConfig(st)
            const isActive = project.status === st
            return (
              <button
                key={st}
                onClick={() => updateStatusMutation.mutate(st)}
                className={`font-sans text-xs px-3 py-2 min-h-[36px] border transition-colors ${
                  isActive
                    ? 'border-cinema-gold/50 text-cinema-gold bg-white/[0.04]'
                    : 'border-white/10 text-cinema-muted hover:border-white/20 hover:text-white'
                }`}
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
              >
                {cfg.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Workspace + context rail. The workspace is the primary working area;
          learning context and submission sit in a deliberate secondary rail so
          the student's files are the first thing on the page. Collapses to a
          single column on narrower screens, workspace first. (Slice 14 working
          flow preserved: read assignment → work on files → submit.) */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-10 xl:gap-12">

      {/* ─── PROJECT WORKSPACE — the primary experience ─────────────────────
          A live view of the real project folder on this workstation. Folders
          navigate; files open in their native application. Nothing here is
          stored by Digital Lab — every row comes from the filesystem on each
          request, which is why the view cannot drift from what is on disk. */}
      <div className="xl:col-span-8 min-w-0">

        {/* Work drive unavailable. Showing an empty folder without saying why
            reads as "my files are gone" and invites a student to redo work that
            still exists on a disconnected drive. */}
        {storageUnavailable && (
          <div className="border-l-2 border-amber-500/60 bg-amber-500/[0.06] px-4 py-3 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle size={16} className="text-amber-400 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="font-sans text-sm text-white font-medium" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  This workstation's SMA work drive is unavailable
                </p>
                <p className="font-sans text-xs text-cinema-soft mt-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  {storage.message ||
                    'Your existing work has not been lost, but it cannot be shown right now. Please tell your trainer before starting new work.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Location + actions. The breadcrumb starts at the project name — the
            student never sees a drive, a path, or a workspace identifier. */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
          <div className="flex items-center gap-2 min-w-0 flex-wrap">
            <FolderOpen size={14} className="text-cinema-gold/60 shrink-0" />
            <button
              onClick={() => setCurrentPath('')}
              className="font-sans text-xs text-cinema-soft hover:text-cinema-gold transition-colors truncate max-w-[40vw]"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            >
              Project files
            </button>
            {pathParts.map((part, idx) => (
              <span key={idx} className="flex items-center gap-2 min-w-0">
                <span className="text-cinema-muted/40 text-xs">/</span>
                <button
                  onClick={() => setCurrentPath(pathParts.slice(0, idx + 1).join('/'))}
                  className="font-sans text-xs text-cinema-muted hover:text-cinema-gold transition-colors truncate max-w-[30vw]"
                  style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                >
                  {part}
                </button>
              </span>
            ))}
          </div>

          {workspaceInitialized && (
            <div className="flex items-center gap-1 shrink-0">
              {currentPath && (
                <button onClick={navigateUp} className="p-2 text-cinema-muted hover:text-cinema-gold transition-colors" title="Go up">
                  <ArrowLeft size={16} />
                </button>
              )}
              <button onClick={() => refetchListing()} className="p-2 text-cinema-muted hover:text-cinema-gold transition-colors" title="Refresh">
                <RefreshCw size={16} />
              </button>
              <label className="p-2 text-cinema-muted hover:text-cinema-gold transition-colors cursor-pointer" title="Add a file">
                <Upload size={16} />
                <input type="file" className="hidden" onChange={(e) => setUploadFile(e.target.files[0])} />
              </label>
            </div>
          )}
        </div>

        {/* Upload confirmation */}
        {uploadFile && workspaceInitialized && (
          <div className="flex items-center justify-between gap-3 flex-wrap border-t border-b border-white/5 py-3 mb-5">
            <span className="font-sans text-sm text-cinema-soft min-w-0 truncate" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {uploadFile.name} · {formatBytes(uploadFile.size)}
            </span>
            <div className="flex items-center gap-3 shrink-0">
              <button
                onClick={handleUpload}
                disabled={uploadFileMutation.isPending}
                className="cinema-btn-primary text-xs px-4 py-2 min-h-[36px] disabled:opacity-50"
              >
                {uploadFileMutation.isPending ? 'Adding…' : 'Add to project'}
              </button>
              <button onClick={() => setUploadFile(null)} className="cinema-btn-text text-xs">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* New folder */}
        {showCreateFolder && workspaceInitialized && (
          <div className="flex items-center gap-3 flex-wrap border-t border-b border-white/5 py-3 mb-5">
            <FolderPlus size={16} className="text-cinema-gold/60 shrink-0" />
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Folder name"
              className="flex-1 min-w-[12rem] px-3 py-2 text-sm bg-white/[0.03] border border-white/10 text-white placeholder:text-cinema-muted/60 focus:outline-none focus:border-cinema-gold/50"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newFolderName.trim()) createFolderMutation.mutate(newFolderName.trim())
              }}
              autoFocus
            />
            <button
              onClick={() => newFolderName.trim() && createFolderMutation.mutate(newFolderName.trim())}
              disabled={createFolderMutation.isPending || !newFolderName.trim()}
              className="cinema-btn-primary text-xs px-4 py-2 min-h-[36px] disabled:opacity-50"
            >
              {createFolderMutation.isPending ? 'Creating…' : 'Create'}
            </button>
            <button
              onClick={() => { setShowCreateFolder(false); setNewFolderName('') }}
              className="cinema-btn-text text-xs"
            >
              Cancel
            </button>
          </div>
        )}

        {/* Preparing the workspace */}
        {!workspaceInitialized && initWorkspaceMutation.isPending && (
          <div className="cinema-empty py-14">
            <Loader2 size={28} className="mx-auto mb-4 text-cinema-gold/60 animate-spin" />
            <p className="font-sans text-sm text-cinema-muted" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Opening your project…
            </p>
          </div>
        )}
        {!workspaceInitialized && initWorkspaceMutation.isError && (
          <div className="cinema-empty py-14">
            <AlertCircle size={28} className="mx-auto mb-4 text-red-400/70" />
            <p className="font-sans text-sm text-cinema-soft" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              We couldn't open your project workspace.
            </p>
            <button
              onClick={() => initWorkspaceMutation.mutate()}
              disabled={!workspaceToken}
              className="cinema-btn-ghost text-sm px-6 py-3 min-h-[44px] mt-5 disabled:opacity-50"
            >
              <RefreshCw size={16} /> Try again
            </button>
          </div>
        )}

        {/* Listing states */}
        {workspaceInitialized && listingLoading && (
          <div className="py-14 text-center">
            <Loader2 size={24} className="mx-auto text-cinema-gold/60 animate-spin" />
          </div>
        )}
        {workspaceInitialized && listingError && !listingLoading && (
          <div className="cinema-empty py-14">
            <AlertCircle size={28} className="mx-auto mb-4 text-red-400/70" />
            <p className="font-sans text-sm text-cinema-soft" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {listingError.message}
            </p>
          </div>
        )}
        {workspaceInitialized && !listingLoading && !listingError && entries.length === 0 && (
          <div className="cinema-empty py-14">
            <FolderOpen size={32} className="mx-auto mb-4 text-cinema-muted/50" />
            <p className="font-sans text-sm text-cinema-soft" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {currentPath ? 'This folder is empty.' : 'This project is ready for your work.'}
            </p>
            <p
              className="font-sans text-xs text-cinema-muted mt-2 max-w-md mx-auto"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            >
              Save a file here from Photoshop, Premiere or any other application
              and it will appear in this list.
            </p>
          </div>
        )}

        {/* ── FOLDERS ───────────────────────────────────────────────────── */}
        {workspaceInitialized && !listingLoading && !listingError && folderEntries.length > 0 && (
          <section className="mb-10">
            <span className="cinema-label block mb-4">Folders</span>
            <div className="cinema-list">
              {folderEntries.map((entry) => (
                <div
                  key={`d-${entry.name}`}
                  className="cinema-list-row group px-3 -mx-3 border-l border-transparent hover:border-cinema-gold/40 hover:bg-white/[0.025] transition-colors"
                >
                  <Folder size={18} className="text-cinema-gold/70 group-hover:text-cinema-gold transition-colors shrink-0" />
                  <button
                    type="button"
                    onClick={() => navigateInto(entry.name)}
                    className="flex-1 min-w-0 text-left"
                  >
                    <span className="block font-sans text-sm text-white font-medium truncate">{entry.name}</span>
                    <span
                      className="block font-sans text-xs text-cinema-muted mt-0.5"
                      style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                    >
                      Updated {formatDate(entry.modified)}
                    </span>
                  </button>
                  <button
                    onClick={() => {
                      const itemPath = `${currentFolderPath}/${entry.name}`
                      if (confirm(`Delete "${entry.name}" and everything inside it?`)) deleteMutation.mutate(itemPath)
                    }}
                    className="p-1.5 text-cinema-muted hover:text-red-400 transition-colors opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:focus:opacity-100 shrink-0"
                    title="Delete"
                  >
                    <Trash2 size={15} />
                  </button>
                  <ChevronRight size={16} className="text-cinema-muted/30 group-hover:text-cinema-gold/60 transition-colors shrink-0" />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── FILES ─────────────────────────────────────────────────────── */}
        {workspaceInitialized && !listingLoading && !listingError && fileEntries.length > 0 && (
          <section className="mb-10">
            <span className="cinema-label block mb-4">Files</span>
            <div className="cinema-list">
              {fileEntries.map((entry) => {
                const isOpening = opening === entry.name
                return (
                  <div
                    key={`f-${entry.name}`}
                    className="cinema-list-row group px-3 -mx-3 border-l border-transparent hover:border-cinema-gold/40 hover:bg-white/[0.025] transition-colors"
                  >
                    <File size={18} className="text-cinema-muted group-hover:text-cinema-gold transition-colors shrink-0" />
                    <button
                      type="button"
                      onClick={() => openFile(entry, currentFolderPath)}
                      disabled={isOpening}
                      className="flex-1 min-w-0 text-left disabled:cursor-wait"
                      title="Open"
                    >
                      <span className="block font-sans text-sm text-white font-medium truncate">{entry.name}</span>
                      <span
                        className="block font-sans text-xs text-cinema-muted mt-0.5 truncate"
                        style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                      >
                        {fileTypeLabel(entry.name)} · {formatBytes(entry.size)} · {formatDate(entry.modified)}
                      </span>
                    </button>
                    <div className="flex items-center gap-1 shrink-0 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-within:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleDownload(`${currentFolderPath}/${entry.name}`)}
                        className="p-1.5 text-cinema-muted hover:text-cinema-gold transition-colors"
                        title="Download"
                      >
                        <Download size={15} />
                      </button>
                      <button
                        onClick={() => {
                          const itemPath = `${currentFolderPath}/${entry.name}`
                          if (confirm(`Delete "${entry.name}"?`)) deleteMutation.mutate(itemPath)
                        }}
                        className="p-1.5 text-cinema-muted hover:text-red-400 transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                    {isOpening ? (
                      <Loader2 size={16} className="text-cinema-glow animate-spin shrink-0" />
                    ) : (
                      <ChevronRight size={16} className="text-cinema-muted/30 group-hover:text-cinema-gold/60 transition-colors shrink-0" />
                    )}
                  </div>
                )
              })}
            </div>
          </section>
        )}

        {/* ── Workspace actions ─────────────────────────────────────────── */}
        {workspaceInitialized && (
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 pt-6 border-t border-white/5">
            <button onClick={() => setShowCreateFolder((v) => !v)} className="cinema-btn-text">
              <FolderPlus size={16} /> New Folder
            </button>

            {/* Escape hatch to Windows. Rendered ONLY when this workstation's
                Agent reports the capability as enabled — it is deliberately
                disabled while every student shares one Windows account, since
                Explorer cannot then be confined to one student's folder. */}
            {explorerEnabled && (
              <button onClick={() => revealFolder(currentFolderPath)} disabled={revealing} className="cinema-btn-text disabled:opacity-50">
                <ExternalLink size={16} /> Open in Windows Explorer
              </button>
            )}
          </div>
        )}
      </div>

      {/* Secondary rail — quiet context, never competing with the files */}
      <div className="xl:col-span-4 min-w-0 space-y-10">
        <ProjectContext projectId={id} />
        <SubmissionSection projectId={id} />
      </div>

      </div>
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function DigitalLabProjects() {
  const [deviceCode, setDeviceCode] = useState(null)
  const [agentReachable, setAgentReachable] = useState(false)
  const { id } = useParams()

  // Detect Agent
  useEffect(() => {
    fetchDeviceCode()
      .then((code) => {
        setDeviceCode(code)
        setAgentReachable(true)
      })
      .catch(() => setAgentReachable(false))
  }, [])

  if (!agentReachable && !id) {
    return (
      <div className="w-full max-w-2xl mx-auto space-y-6">
        <div className="page-header">
          <div>
            <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium mb-3 block">
              Student · Digital Lab
            </span>
            <h1 className="text-2xl sm:text-3xl text-gray-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800 }}>
              My Projects
            </h1>
          </div>
        </div>
        <div className="empty-state py-16">
          <AlertCircle className="w-12 h-12 text-orange-400 dark:text-orange-500 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-300 text-lg" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            SMA Computer Agent not detected on this workstation.
          </p>
          <p className="text-gray-400 dark:text-gray-500 text-sm mt-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Projects require the SMA Computer Agent to manage local workspace files.
          </p>
          <Link to="/student/digital-lab" className="inline-flex items-center gap-2 mt-6 text-sm text-orange-600 dark:text-orange-400 hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to Digital Lab
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full max-w-[1600px] mx-auto">
      {id ? <ProjectDetail deviceCode={deviceCode} /> : <ProjectList deviceCode={deviceCode} />}
    </div>
  )
}
